import { Fragment, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import { createFeedback } from '../store/feedback.slice'
import { Status } from '../components/status'
import { capitalize } from '../utils/functions'
import { FEEDBACK_RATINGS, SUBMIT_OPTIONS, TEXT_AREA_ROWS } from '../utils/constants'
import { NewFeedbackSchema } from '../utils/types/feedback'
import SVG from '../assets/undraw_walking-outside_7jfy.svg'

export const Feedback = () => {
  const [feedback, setFeedback] = useState<NewFeedbackSchema>({
    rating: 0,
    info: '',
    submittedAs: 'visitor',
    meta: {}
  })
  const [isFeedbackOptionsOpen, setIsFeedbackOptionsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ error: '' })
  const feedbackOptionsTriggerRef = useRef(null)
  const feedbackOptionsContextRef = useRef(null)

  useOutsideClick({ element: feedbackOptionsContextRef, trigger: feedbackOptionsTriggerRef }, () =>
    setIsFeedbackOptionsOpen(false)
  )

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const ctxQuery = searchParams.get('ctx') ?? ''

  useEffect(() => {
    authUser && setFeedback({ ...feedback, submittedAs: 'user' })
    // eslint-disable-next-line
  }, [authUser, feedback.rating])

  useEffect(() => {
    let normalizedCtx = ctxQuery

    if (normalizedCtx !== 'global' && normalizedCtx !== 'submit') {
      normalizedCtx = ''
    }

    setFeedback({
      ...feedback,
      meta: { ...feedback.meta, context: normalizedCtx }
    })
    // eslint-disable-next-line
  }, [ctxQuery])

  useEffect(() => {
    if (authUser && feedback.submittedAs === 'user') {
      setFeedback({
        ...feedback,
        meta: { ...feedback.meta, name: authUser.name, email: authUser.email }
      })
    }

    if (feedback.submittedAs === 'anonymous') {
      setFeedback({
        ...feedback,
        meta: { ...feedback.meta, name: '', email: '' }
      })
    }

    // eslint-disable-next-line
  }, [authUser, feedback.submittedAs])

  const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback({ ...feedback, submittedAs: e.target.id })
    setIsFeedbackOptionsOpen(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) =>
    setFeedback({ ...feedback, [e.target.id]: e.target.value })

  const handleMetaInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFeedback({
      ...feedback,
      meta: { ...feedback.meta, [e.target.id]: e.target.value }
    })

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    try {
      setIsLoading(true)

      const { status } = await dispatch(createFeedback(feedback)).unwrap()

      if (status === 201) {
        navigate(authUser ? '/dashboard' : '/', { viewTransition: true })
      }
    } catch (error: any) {
      setRequestState({ error: error.message })
      setIsLoading(false)
    }
  }

  return (
    <div className='main_content experience_content'>
      {requestState.error && (
        <Status
          error={requestState.error}
          reset={() => setRequestState({ ...requestState, error: '' })}
        />
      )}
      <h1 className='heading-styled'>How is your experience?</h1>
      <div className='rating-picker'>
        {FEEDBACK_RATINGS.map((rating) => (
          <span
            key={rating}
            className={`material-symbols-rounded ${rating <= feedback.rating ? 'material-symbols-filled' : ''}`}
            onClick={() => setFeedback({ ...feedback, rating })}>
            star
          </span>
        ))}
      </div>
      {!!feedback.rating && (
        <>
          <form onSubmit={handleSubmit} className='form-grid form-grid-sm'>
            <label htmlFor='name'>Submit as:</label>
            <div className='select-custom'>
              <div
                id='feedback_options_trigger'
                className='input_container'
                onClick={() => setIsFeedbackOptionsOpen(!isFeedbackOptionsOpen)}
                ref={feedbackOptionsTriggerRef}>
                <span id='feedback_options_trigger'>
                  {authUser && feedback.submittedAs === 'user'
                    ? authUser.name
                    : capitalize(feedback.submittedAs)}
                </span>
                <span id='feedback_options_trigger' className='material-symbols-rounded'>
                  {isFeedbackOptionsOpen ? 'arrow_drop_up' : 'arrow_drop_down'}
                </span>
              </div>
              {isFeedbackOptionsOpen && (
                <div
                  className='context_menu context_menu-down context_menu-grid context_menu-grid-alt'
                  ref={feedbackOptionsContextRef}>
                  {SUBMIT_OPTIONS.map((option) => {
                    if (
                      (authUser && option.name === 'visitor') ||
                      (!authUser && option.name === 'user')
                    ) {
                      return <Fragment key={option.name} />
                    }

                    return (
                      <Fragment key={option.name}>
                        <input
                          id={option.name}
                          type='checkbox'
                          onChange={handleTypeChange}
                          hidden
                        />
                        <label
                          htmlFor={option.name}
                          className={`${feedback.submittedAs === option.name ? 'context_menu-grid-selected' : ''}`}>
                          <div>
                            <div>
                              <span className='material-symbols-rounded'>{option.icon}</span>
                              <h3>
                                {authUser && option.name === 'user'
                                  ? authUser.name
                                  : capitalize(option.name)}
                              </h3>
                            </div>
                          </div>
                        </label>
                      </Fragment>
                    )
                  })}
                </div>
              )}
            </div>
            {feedback.submittedAs === 'visitor' && (
              <>
                <label htmlFor='name'>
                  Name:
                  <em className='required'>*</em>
                </label>
                <input
                  id='name'
                  defaultValue={feedback.meta.name}
                  onChange={handleMetaInputChange}
                  placeholder='Enter name...'
                  required
                  autoComplete='off'
                />
                <label htmlFor='email'>Email:</label>
                <input
                  id='email'
                  type='email'
                  defaultValue={feedback.meta.email}
                  onChange={handleMetaInputChange}
                  placeholder='example@abc.com'
                />
              </>
            )}
            <label />
            <textarea
              id='info'
              maxLength={300}
              value={feedback.info}
              onChange={handleInputChange}
              rows={TEXT_AREA_ROWS}
              placeholder='More information (optional)...'
            />
            <button className='button-with-icon cta' disabled={isLoading}>
              {isLoading ? (
                <span>Please wait...</span>
              ) : (
                <>
                  <span>Submit feedback</span>
                  <span className='material-symbols-rounded material-symbols-sm'>
                    sentiment_satisfied
                  </span>
                </>
              )}
            </button>
          </form>
          <div className='svg_container'>
            <img src={SVG} alt='Free walking in spring' className='img-lg' />
          </div>
        </>
      )}
    </div>
  )
}
